<template>
  <div id="app">
    <TheNavbar />
    <DemoUserAlert v-if="$route.path !== '/expired'" />
    <router-view />
    <Messages />
    <TheFooter />
  </div>
</template>

<script>
import TheNavbar from './components/TheNavbar';
import DemoUserAlert from './components/DemoUserAlert';
import Messages from './components/Messages';
import TheFooter from './components/TheFooter';

export default {
  name: 'App',
  components: {
    TheNavbar,
    DemoUserAlert,
    Messages,
    TheFooter,
  },
};
</script>

<style lang="scss">
// =============================================================================
// Global theme tokens + base (Phase 1 UI modernization).
// CSS custom properties mirror the SCSS $colors map so plain-CSS component
// styles and future pages can consume the same palette.
// =============================================================================
:root {
  --color-primary: #{color(primary-500)};
  --color-primary-600: #{color(primary-600)};
  --color-primary-700: #{color(primary-700)};
  --color-primary-50: #{color(primary-50)};
  --color-ink: #{color(ink)};
  --color-slate-700: #{color(slate-700)};
  --color-slate-600: #{color(slate-600)};
  --color-slate-500: #{color(slate-500)};
  --color-slate-400: #{color(slate-400)};
  --color-slate-200: #{color(slate-200)};
  --color-slate-100: #{color(slate-100)};
  --color-slate-50: #{color(slate-50)};
  --color-surface: #{color(surface)};
  --color-surface-muted: #{color(surface-muted)};
  --color-border: #{color(border)};
  --color-border-strong: #{color(border-strong)};
  --color-success: #{color(success)};
  --color-danger: #{color(danger)};
  --radius-md: #{$radius-md};
  --radius-lg: #{$radius-lg};
  --shadow-sm: #{$shadow-sm};
  --shadow-md: #{$shadow-md};
  --shadow-lg: #{$shadow-lg};
  --font-sans: #{$font-sans};
}

#app {
  display: flex;
  flex-direction: column;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: color(ink);
  min-height: 100vh;
  font-family: $font-sans;
  font-size: $text-base;
  line-height: 1.55;
  background: color(surface-muted);
}

body {
  font-family: $font-sans;
  color: color(ink);
  background: color(surface-muted);
}

html {
  scroll-behavior: smooth;
  overflow-x: hidden;
  width: 100vw;
  margin: 0;
  padding: 0;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: $font-sans;
  color: color(ink);
  font-weight: $weight-bold;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h1 {
  font-size: $text-3xl;
}
h3 {
  font-size: $text-xl;
  font-weight: $weight-semibold;
}

a {
  color: color(primary-600);
  transition: color $transition-fast;
}
a:hover {
  color: color(primary-700);
}

// --- Bootstrap / Bootswatch primitive overrides (used across these pages) ----
// Scoped under #app so they reliably win over Bootswatch Lux (imported in
// main.js) and the CDN Bootstrap 5 stylesheet loaded in index.html.
#app .form-control {
  height: auto;
  font-family: $font-sans;
  font-size: $text-sm;
  color: color(ink);
  background-color: color(surface);
  border: 1px solid color(border-strong);
  border-radius: $radius-md;
  padding: 0.625rem 0.875rem;
  box-shadow: none;
  transition: border-color $transition-fast, box-shadow $transition-fast;

  &::placeholder {
    color: color(slate-400);
  }
  &:focus {
    color: color(ink);
    background-color: color(surface);
    border-color: color(primary-500);
    box-shadow: 0 0 0 3px rgba(0, 155, 218, 0.18);
  }
  &:disabled,
  &[readonly] {
    background-color: color(slate-100);
    color: color(slate-400);
  }
}

#app .btn {
  font-family: $font-sans;
  font-weight: $weight-semibold;
  font-size: $text-sm;
  border-radius: $radius-md;
  padding: 0.6875rem 1.25rem;
  transition: background-color $transition-fast, border-color $transition-fast,
    color $transition-fast, box-shadow $transition-fast;
  box-shadow: none;
  text-transform: none;

  &:focus,
  &.focus {
    box-shadow: 0 0 0 3px rgba(0, 155, 218, 0.3);
  }
  &:disabled,
  &.disabled {
    opacity: 0.55;
    box-shadow: none;
  }
}

#app .btn.btn-primary {
  background-color: color(primary-500);
  border-color: color(primary-500);
  color: #fff;

  &:hover,
  &:focus {
    background-color: color(primary-600);
    border-color: color(primary-600);
    color: #fff;
  }
  &:not(:disabled):not(.disabled):active,
  &:not(:disabled):not(.disabled):hover {
    background-color: color(primary-600);
    border-color: color(primary-600);
  }
}

#app .btn.btn-secondary {
  background-color: color(surface);
  border-color: color(border-strong);
  color: color(slate-700);

  &:hover,
  &:focus {
    background-color: color(slate-50);
    border-color: color(slate-400);
    color: color(ink);
  }
}

#app .link-primary {
  color: color(primary-600);
  &:hover {
    color: color(primary-700);
  }
}

#app .breadcrumb {
  background: transparent;
  padding: 0;
  margin-bottom: $space-3;
  font-size: $text-sm;

  .breadcrumb-item {
    color: color(slate-500);
    &.active {
      color: color(slate-600);
      font-weight: $weight-medium;
    }
  }
}

/* BUTTON WITH CORNER COLOR FLIP FX NEEDS BOTH mediataloBtnFx AND cornerFx CLASSES TO WORK */
.mediataloBtnFx {
  color: black !important;
  outline: none;
  background: transparent;
  line-height: 2;
  position: relative;
  display: inline-block;
  cursor: pointer;
  text-decoration: none;
  overflow: hidden;
  transition: all 0.5s;
}

.mediataloBtnFx:before,
.mediataloBtnFx:after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  background: black;
  z-index: -1;
  transform: translate3D(0, -100%, 0);
  transition: all 0.5s;
}

.mediataloBtnFx:before {
  background: color(primary) !important;
  transform: translate3D(0, 0, 0);
}

.mediataloBtnFx:enabled:hover {
  color: white !important;
}
.mediataloBtnFx:disabled:hover {
  color: black;
  cursor: default;
}

.mediataloBtnFx:enabled:hover:after {
  transform: translate3D(0, 0, 0);
  transition: all 0.5s;
}

.cornerFx:enabled:after {
  width: 200%;
  transform-origin: 0 bottom;
  transform: translate3D(-50%, 0, 0) rotate(-50deg);
}

.cornerFx:enabled:hover:after {
  transform: rotate(0deg);
  transition: all 0.5s;
}

/* NORMAL BUTTON FX */
.mediataloBtn {
  background: color(primary) !important;
  color: #2c3e50 !important;
  transition: all 0.5s;

  &:hover {
    background: black !important;
    color: white !important;
  }
}

.btn-sm {
  font-size: 12px;
}

select {
  height: 48px;
}

.close {
  cursor: pointer;
}

.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 25px;
  background: color(gray);
  outline: none;
  opacity: 0.7;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
  padding: 0px;
}

.slider:hover {
  opacity: 1;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 5px;
  height: 25px;
  background: color(primary);
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 5px;
  height: 25px;
  background: color(primary);
  cursor: pointer;
}

.help:hover {
  cursor: pointer;
}

.trash,
.add,
.preview,
.results,
.edit {
  width: 40px;
  height: 40px;
  border-radius: 10%;
}
.add {
  padding: 2px;
}
.preview,
.results {
  padding: 3px;
}
.trash,
.edit {
  padding: 4px;
}
.trash:hover,
.add:hover,
.preview:hover,
.results:hover,
.edit:hover {
  background: color(gray);
}

.sub-heading {
  color: color(primary);
}

// input,
// select {
//   background: color(very-light-gray);
//   padding: 1vw 1vw;
//   font-weight: 500;
//   border: none;
//   border-radius: 2%;
// }
* {
  &:focus,
  &:active {
    outline: none;
  }
}
select {
  background: color(very-light-gray);
  border: none;
  padding: 0;
  padding-left: 1vw;
}

// Button groups
.button-group {
  display: flex;
  flex-wrap: wrap;
  @include desktop() {
    display: block;
  }
  .button.active {
    background: color(light-primary);
    color: black;
    box-shadow: 0px 0px 0px 1px black;
  }
  @include button;
}
@include button;
</style>
